import {
  useDeleteMutation,
  useFindByQuery,
  useUpdateMutation,
} from '@gripp/shared-logic';
import {
  Colors,
  ControlledTextInput,
  CustomAlert,
  SaveFormTop,
  TopModalBar,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAudioPlayer } from 'expo-audio';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const audioSource = require('../../../static-assets/delete_pop.mp3');

type EditWorkspaceFormProps = {
  onComplete: () => void;
  workspaceId: string;
};

export const EditWorkspaceForm = ({
  onComplete,
  workspaceId,
}: EditWorkspaceFormProps) => {
  const { t } = useTranslation();
  const translationPath = 'workspace.deleteWorkspace';

  const filter = useMemo(() => {
    return {
      id: { eq: workspaceId },
    };
  }, [workspaceId]);

  const { data, isLoading: isNameLoading } = useFindByQuery({
    modelName: 'workspace',
    query: FIND_WORKSPACE,
    variables: { filter },
  });

  const [workspaceName, setWorkspaceName] = useState('');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const player = useAudioPlayer(audioSource);

  const { mutateAsync, isPending } = useUpdateMutation({
    modelName: 'workspace',
    query: UPDATE_WORKSPACE,
  });

  const {
    mutateAsync: deleteWorkspaceAsync,
    isPending: isDeleteWorkspacePending,
  } = useDeleteMutation({
    modelName: 'workspace',
    query: DELETE_WORKSPACE,
  });

  const formSchema = yup.object({
    name: yup.string().required('Workspace name is required'),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: { name: workspaceName },
  });

  useEffect(() => {
    if (!data || !data.items.length) return;

    reset({ name: data.items[0].name });
    setWorkspaceName(data.items[0].name);
  }, [data]);

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input = {
      id: workspaceId,
      name: data.name,
    };

    await mutateAsync(
      { input },
      {
        onSettled: () => {
          onComplete();
        },
      }
    );
  };

  const onWorkspaceDelete = async () => {
    const input = {
      id: workspaceId,
    };

    await deleteWorkspaceAsync({ input });

    onComplete();
    showToast();
    player.play();
  };

  const showToast = () => {
    Toast.show({
      type: 'blackToast',
      text1: 'Woosh!',
      text2: `${workspaceName} has been deleted.`,
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
  };

  return (
    !isNameLoading && (
      <>
        <View style={{ flex: 1, width: '100%' }}>
          <SaveFormTop
            onSubmit={handleSubmit(onSubmit)}
            title={workspaceName}
            topBar={TopModalBar}
            isDirty={isDirty}
            onCancel={onComplete}
          >
            <View style={styles.innerFormContainer}>
              <ControlledTextInput
                control={control}
                name="name"
                placeholder={t('workspace.create.name')}
              />
            </View>
            <View style={styles.bottomContainer}>
              <Pressable
                style={styles.deleteButton}
                onPress={() => {
                  setShowDeleteAlert(true);
                }}
              >
                <Text style={styles.deleteButtonLabel}>
                  {t(`${translationPath}.deleteWorkspace`)}
                </Text>
              </Pressable>
            </View>
          </SaveFormTop>
        </View>
        <CustomAlert
          visible={showDeleteAlert}
          hideAlert={() => setShowDeleteAlert(false)}
          title={
            <>
              {t(`${translationPath}.alertBody1`)} <b>{workspaceName}</b>?{' '}
            </>
          }
          primaryButtonLabel={t(`${translationPath}.delete`)}
          secondaryButtonLabel={t(`${translationPath}.cancel`)}
          action={() => onWorkspaceDelete()}
          containerStyle={styles.deleteAlertContainer}
        />
      </>
    )
  );
};

const styles = StyleSheet.create({
  innerFormContainer: {
    margin: 20,
  },
  bottomContainer: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: Colors.graySpacer,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    color: Colors.danger,
  },
  deleteAlertContainer: {
    width: 350,
  },
});

const UPDATE_WORKSPACE = `
  mutation UpdateWorkspace($input: MutateWorkspaceInput!) {
  updateWorkspace(input: $input) {
    id
  }
}  
`;

const DELETE_WORKSPACE = `
mutation DeleteWorkspace($input: MutateWorkspaceInput!) {
  deleteWorkspace(input: $input) {
    id
  }
}
`;

const FIND_WORKSPACE = `
  query FindWorkspaces($filter: WorkspaceFilter) {
  findWorkspaces(filter: $filter) {
    items {
      id
      name
    }
  }
}
`;
